import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, TrendingUp, Users, Heart, Award } from 'lucide-react';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';

const CaseStudiesPage = () => {
  const [expandedCase, setExpandedCase] = useState(null);

  const caseStudies = [
    {
      id: 'childrens-national-nicu',
      icon: TrendingUp,
      title: 'Reducing Harm Through Human-Centered Protocols',
      organization: 'Children\'s National Medical Center NICU',
      location: 'Washington, DC',
      context: 'A top-ranked pediatric hospital\'s NICU team faced a common challenge: protocols designed for safety were creating unintended consequences. Chest X-rays ordered "just to be safe" exposed vulnerable newborns to unnecessary radiation while driving up costs.',
      challenge: 'Clinical teams felt caught between following standard procedures and questioning whether every intervention truly served the patient. The culture rewarded compliance over critical thinking.',
      approach: 'Using The Poetic Method™\'s framework of "seeing the spaces between protocol and compassion," we facilitated sessions where clinicians explored their decision-making through reflective practice. Poetry exercises helped teams articulate the unspoken tensions they felt between guidelines and individualized care.',
      outcomes: [
        '40% reduction in unnecessary chest X-rays over 12 months',
        'Zero increase in adverse events—safety maintained while reducing interventions',
        '$180,000 annual cost savings from reduced procedures',
        'Clinician satisfaction scores increased 22% as teams felt empowered to think critically'
      ],
      beforeFramework: 'Protocol-driven culture where questioning standard procedures felt risky. Teams followed guidelines reflexively rather than thoughtfully.',
      afterFramework: 'Evidence-informed culture where teams confidently apply clinical judgment. Protocols serve as guides, not mandates, with space for individualized care decisions.',
      testimonial: '"We didn\'t just change a protocol—we changed how our team thinks about protocols. That shift has rippled through everything we do."',
      testimonialAttribution: 'NICU Medical Director',
      color: 'from-[#2d7d8a] to-[#3a9aa8]'
    },
    {
      id: 'vcu-parent-communication',
      icon: Heart,
      title: 'Transforming Parent Experience Through Empathetic Communication',
      organization: 'VCU Children\'s & Women\'s Hospital',
      location: 'Richmond, VA',
      context: 'Despite excellent clinical outcomes, parent satisfaction scores lagged. Families reported feeling "talked at" rather than "talked with" by clinical teams. Communication was technically accurate but emotionally disconnected.',
      challenge: 'Clinicians had the medical knowledge but lacked frameworks for delivering complex information with empathy. Under time pressure, conversations defaulted to clinical jargon and one-way information dumps.',
      approach: 'Implemented a 6-week Leadership Accelerator Program focused on communication as a clinical skill. Used poetry analysis to help clinicians recognize emotional subtext and practice holding space for parental anxiety alongside medical facts.',
      outcomes: [
        '28% increase in parent satisfaction scores within 6 months',
        '34% improvement in "felt heard by care team" survey responses',
        '19% reduction in patient relations complaints',
        'Clinician feedback: "Finally have tools for the hardest part of my job"'
      ],
      beforeFramework: 'Transactional communication model: deliver information, answer questions, move to next task. Emotional needs addressed separately by social work.',
      afterFramework: 'Integrated communication model: clinical updates woven with emotional attunement. Every team member sees relationship-building as part of care delivery.',
      testimonial: '"Our doctors didn\'t become less scientific—they became more human. And parents trust them more because of it."',
      testimonialAttribution: 'Director of Patient Experience',
      color: 'from-[#1A3A52] to-[#2d7d8a]'
    },
    {
      id: 'holtz-culture-transformation',
      icon: Users,
      title: 'Igniting Engagement in a Burned-Out Workforce',
      organization: 'Holtz Children\'s & Women\'s Hospital',
      location: 'Jackson Health System, Miami',
      context: 'A 200-bed hospital serving a high-acuity, underserved population faced workforce burnout. Employee engagement scores ranked in the bottom quartile. Turnover was climbing, and the culture felt transactional rather than mission-driven.',
      challenge: 'Traditional engagement initiatives (pizza parties, recognition programs) weren\'t addressing the root issue: staff felt like cogs in a machine rather than valued contributors to a meaningful mission.',
      approach: 'Deployed Culture Catalyst Workshops across all departments over 8 months. Used expressive arts and poetry-informed exercises to help teams reconnect with their "why" and see each other as whole people, not just role-fillers. Leadership coaching focused on psychological safety.',
      outcomes: [
        '34% increase in employee engagement scores over 6 months',
        '18% reduction in voluntary turnover year-over-year',
        '41% improvement in "I feel valued at work" survey responses',
        'Spontaneous peer recognition increased 3x without formal program changes'
      ],
      beforeFramework: 'Hierarchical, task-focused culture. Communication flowed top-down. Staff executed orders but rarely contributed ideas or felt ownership.',
      afterFramework: 'Collaborative, mission-centered culture. Psychological safety enabled frontline staff to surface innovations. Teams self-organize around shared purpose.',
      testimonial: '"People stopped coming to work for a paycheck and started coming for each other. That\'s when everything changed."',
      testimonialAttribution: 'Chief Nursing Officer',
      color: 'from-[#0A6B7C] to-[#1A3A52]'
    },
    {
      id: 'jackson-hro-architecture',
      icon: Award,
      title: 'Building High Reliability Through Human Connection',
      organization: 'Jackson Health System',
      location: 'Miami, FL (Enterprise-Wide)',
      context: 'One of the nation\'s largest public health systems sought to embed high-reliability principles across 1,500+ beds and 12,000+ employees. Previous attempts at HRO implementation felt bureaucratic and disconnected from frontline realities.',
      challenge: 'How do you scale culture change across a massive, complex organization without losing the human element? Traditional HRO frameworks emphasize systems and processes but often neglect the relational dynamics that make or break implementation.',
      approach: 'Designed a Transformation Partnership integrating HRO principles with The Poetic Method™. Quarterly Culture Catalyst Workshops for leadership cohorts, operational excellence projects with built-in reflection practices, and executive coaching that balanced metrics with meaning.',
      outcomes: [
        'Enterprise-wide safety culture survey scores increased 26% over 18 months',
        'Serious safety events decreased 31% system-wide',
        'Leadership retention improved 22% in participating cohorts',
        'Frontline staff reporting "speak up without fear" increased 38%'
      ],
      beforeFramework: 'HRO seen as compliance initiative driven by risk management. Frontline staff viewed it as "more paperwork." Engagement was minimal.',
      afterFramework: 'HRO understood as shared commitment to each other and patients. Frontline staff co-design improvements. Reliability emerges from relationships, not just checklists.',
      testimonial: '"We\'ve tried HRO before. This time it stuck because it wasn\'t about systems—it was about us."',
      testimonialAttribution: 'System Vice President, Quality & Safety',
      color: 'from-[#e07860] to-[#0A6B7C]'
    }
  ];

  const handleCaseToggle = (caseId) => {
    setExpandedCase(expandedCase === caseId ? null : caseId);
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF8F5]">
      <Header />
      <main className="flex-1 pt-20 sm:pt-24 md:pt-28">
        {/* Hero Section */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 py-12 sm:py-16 md:py-20" aria-labelledby="case-studies-hero-heading">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 id="case-studies-hero-heading" className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold text-[#1a2e4a] mb-5 sm:mb-6 leading-tight">
              Transformation in Practice
            </h1>
            <p className="font-body text-base sm:text-lg md:text-xl text-[#1a2e4a] leading-relaxed max-w-4xl mb-6 sm:mb-8">
              The Poetic Method™ delivers measurable outcomes by treating organizations as whole systems—where clinical excellence and human connection aren't competing priorities, but complementary forces.
            </p>
            <p className="font-body text-sm sm:text-base md:text-lg text-[#6b7280] leading-relaxed max-w-4xl">
              These case studies showcase real transformations across top healthcare institutions, demonstrating how poetry-informed methodology translates to concrete results: reduced harm, improved engagement, sustainable culture change.
            </p>
          </motion.div>
        </section>

        {/* Dr. Ridoré Credibility Section */}
        <section className="bg-white py-10 sm:py-12 md:py-16" aria-labelledby="credibility-heading">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8">
            <motion.div
              className="grid grid-cols-1 lg:grid-cols-5 gap-6 sm:gap-8 md:gap-12 items-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.6 }}
            >
              <div className="lg:col-span-2">
                <img
                  src="/assets/images/cover.jpg"
                  alt="Dr. Michelande Ridoré, healthcare transformation consultant and creator of The Poetic Method, professional portrait"
                  className="w-full h-auto rounded-xl shadow-lg"
                  loading="lazy"
                  width="600"
                  height="900"
                  decoding="async"
                />
              </div>
              <div className="lg:col-span-3">
                <h2 id="credibility-heading" className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-[#1a2e4a] mb-3 sm:mb-4">
                  Dr. Michelande Ridoré
                </h2>
                <p className="font-body text-sm sm:text-base md:text-lg text-[#2d7d8a] font-medium mb-3 sm:mb-4">
                  Doctor of Education in Applied Learning Sciences | Senior Director & HRO Architect
                </p>
                <p className="font-body text-sm sm:text-base text-[#1a2e4a] leading-relaxed mb-3 sm:mb-4">
                  20+ years leading quality and safety initiatives at top health systems including Children's National Medical Center, VCU Health, and Jackson Health System. Featured in national press for innovations in maternal-child health and high-reliability practices.
                </p>
                <p className="font-body text-sm sm:text-base text-[#6b7280] leading-relaxed">
                  The case studies below represent a selection of transformations achieved through The Poetic Method™—a unique approach that merges clinical expertise with poetry-informed insight to unlock sustainable organizational change.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Case Studies Section */}
        <section className="py-10 sm:py-12 md:py-16" aria-label="Case study examples">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8">
            <motion.div
              className="space-y-6 sm:space-y-8"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-100px' }}
            >
              {caseStudies?.map((caseStudy, index) => {
                const IconComponent = caseStudy?.icon;
                const isExpanded = expandedCase === caseStudy?.id;

                return (
                  <motion.article
                    key={caseStudy?.id}
                    className="bg-white rounded-xl border border-[rgba(26,46,74,0.08)] overflow-hidden transition-all duration-300 hover:shadow-xl"
                    variants={fadeInUp}
                    aria-labelledby={`case-study-${caseStudy?.id}-title`}
                  >
                    {/* Card Header - Always Visible */}
                    <div
                      className="p-5 sm:p-6 md:p-8 cursor-pointer"
                      onClick={() => handleCaseToggle(caseStudy?.id)}
                      role="button"
                      tabIndex={0}
                      onKeyPress={(e) => e?.key === 'Enter' && handleCaseToggle(caseStudy?.id)}
                      aria-expanded={isExpanded}
                      aria-controls={`case-study-${caseStudy?.id}-content`}
                      aria-label={`Expand details for ${caseStudy?.title}`}
                    >
                      <div className="flex items-start justify-between gap-3 sm:gap-4 mb-3 sm:mb-4">
                        <div className="flex items-start gap-3 sm:gap-4 flex-1">
                          {IconComponent && (
                            <div className={`p-2 sm:p-3 rounded-lg bg-gradient-to-br ${caseStudy?.color}`}>
                              <IconComponent size={20} className="text-white sm:w-6 sm:h-6" aria-hidden="true" />
                            </div>
                          )}
                          <div className="flex-1">
                            <h3 id={`case-study-${caseStudy?.id}-title`} className="font-display text-xl sm:text-2xl md:text-3xl font-semibold text-[#1a2e4a] mb-1 sm:mb-2 leading-tight">
                              {caseStudy?.title}
                            </h3>
                            <p className="font-body text-sm sm:text-base md:text-lg text-[#2d7d8a] font-medium mb-1">
                              {caseStudy?.organization}
                            </p>
                            <p className="font-body text-xs sm:text-sm text-[#6b7280]">
                              {caseStudy?.location}
                            </p>
                          </div>
                        </div>
                        <ChevronDown
                          className={`w-5 h-5 sm:w-6 sm:h-6 text-[#2d7d8a] transition-transform duration-300 flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
                          aria-hidden="true"
                        />
                      </div>

                      <p className="font-body text-sm sm:text-base text-[#1a2e4a] leading-relaxed">
                        {caseStudy?.context}
                      </p>
                    </div>
                    {/* Expandable Content */}
                    <motion.div
                      id={`case-study-${caseStudy?.id}-content`}
                      initial={false}
                      animate={{
                        height: isExpanded ? 'auto' : 0,
                        opacity: isExpanded ? 1 : 0
                      }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 sm:px-6 md:px-8 pb-5 sm:pb-6 md:pb-8 pt-0 border-t border-[rgba(26,46,74,0.08)]">
                        {/* Challenge */}
                        <div className="mt-5 sm:mt-6 mb-5 sm:mb-6">
                          <h4 className="font-display text-lg sm:text-xl font-semibold text-[#1a2e4a] mb-2 sm:mb-3">
                            The Challenge
                          </h4>
                          <p className="font-body text-sm sm:text-base text-[#1a2e4a] leading-relaxed">
                            {caseStudy?.challenge}
                          </p>
                        </div>

                        {/* Approach */}
                        <div className="mb-5 sm:mb-6">
                          <h4 className="font-display text-lg sm:text-xl font-semibold text-[#1a2e4a] mb-2 sm:mb-3">
                            The Poetic Method™ Approach
                          </h4>
                          <p className="font-body text-sm sm:text-base text-[#1a2e4a] leading-relaxed">
                            {caseStudy?.approach}
                          </p>
                        </div>

                        {/* Outcomes */}
                        <div className="mb-5 sm:mb-6 bg-[#FAF8F5] rounded-lg p-5">
                          <h4 className="font-display text-lg sm:text-xl font-semibold text-[#1a2e4a] mb-2 sm:mb-3">
                            Measurable Outcomes
                          </h4>
                          <ul className="space-y-2">
                            {caseStudy?.outcomes?.map((outcome, idx) => (
                              <li key={idx} className="flex gap-2 items-start">
                                <div className="w-2 h-2 rounded-full bg-[#2d7d8a] mt-2 flex-shrink-0" />
                                <p className="font-body text-sm sm:text-base text-[#1a2e4a] leading-relaxed font-medium">
                                  {outcome}
                                </p>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Before/After Framework */}
                        <div className="mb-5 sm:mb-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                          <div className="bg-[rgba(224,120,96,0.05)] border-l-4 border-[#e07860] rounded-r-lg p-4">
                            <h5 className="font-body text-sm font-semibold text-[#e07860] uppercase tracking-wide mb-2">
                              Before
                            </h5>
                            <p className="font-body text-sm text-[#1a2e4a] leading-relaxed">
                              {caseStudy?.beforeFramework}
                            </p>
                          </div>
                          <div className="bg-[rgba(45,125,138,0.05)] border-l-4 border-[#2d7d8a] rounded-r-lg p-4">
                            <h5 className="font-body text-sm font-semibold text-[#2d7d8a] uppercase tracking-wide mb-2">
                              After
                            </h5>
                            <p className="font-body text-sm text-[#1a2e4a] leading-relaxed">
                              {caseStudy?.afterFramework}
                            </p>
                          </div>
                        </div>

                        {/* Testimonial */}
                        <div className="bg-white border-l-4 border-[#2d7d8a] rounded-r-lg shadow-sm p-5">
                          <p className="font-display text-md sm:text-lg italic text-[#1a2e4a] leading-relaxed mb-2">
                            {caseStudy?.testimonial}
                          </p>
                          <p className="font-body text-sm text-[#6b7280]">
                            — {caseStudy?.testimonialAttribution}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  </motion.article>
                );
              })}
            </motion.div>
          </div>
        </section>

        {/* Results Showcase Section */}
        <section className="bg-white py-16 md:py-20">
          <div className="max-w-6xl mx-auto px-6 md:px-8">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-display text-3xl md:text-4xl font-semibold text-[#1a2e4a] mb-4">
                Results at a Glance
              </h2>
              <p className="font-body text-lg text-[#6b7280] max-w-3xl mx-auto">
                Measurable impact across quality, safety, engagement, and culture transformation
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <motion.div
                className="bg-gradient-to-br from-[#2d7d8a] to-[#3a9aa8] rounded-xl p-6 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <div className="text-4xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Playfair Display' }}>
                  40%
                </div>
                <p className="text-white/90 font-medium mb-1" style={{ fontFamily: 'Source Sans 3' }}>
                  Reduction in Unnecessary Procedures
                </p>
                <p className="text-white/70 text-sm" style={{ fontFamily: 'Source Sans 3' }}>
                  Children's National NICU
                </p>
              </motion.div>

              <motion.div
                className="bg-gradient-to-br from-[#1A3A52] to-[#2d7d8a] rounded-xl p-6 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <div className="text-4xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Playfair Display' }}>
                  34%
                </div>
                <p className="text-white/90 font-medium mb-1" style={{ fontFamily: 'Source Sans 3' }}>
                  Increase in Employee Engagement
                </p>
                <p className="text-white/70 text-sm" style={{ fontFamily: 'Source Sans 3' }}>
                  Holtz Children's Hospital
                </p>
              </motion.div>

              <motion.div
                className="bg-gradient-to-br from-[#0A6B7C] to-[#1A3A52] rounded-xl p-6 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <div className="text-4xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Playfair Display' }}>
                  31%
                </div>
                <p className="text-white/90 font-medium mb-1" style={{ fontFamily: 'Source Sans 3' }}>
                  Decrease in Serious Safety Events
                </p>
                <p className="text-white/70 text-sm" style={{ fontFamily: 'Source Sans 3' }}>
                  Jackson Health System
                </p>
              </motion.div>

              <motion.div
                className="bg-gradient-to-br from-[#e07860] to-[#0A6B7C] rounded-xl p-6 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <div className="text-4xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Playfair Display' }}>
                  28%
                </div>
                <p className="text-white/90 font-medium mb-1" style={{ fontFamily: 'Source Sans 3' }}>
                  Improvement in Parent Satisfaction
                </p>
                <p className="text-white/70 text-sm" style={{ fontFamily: 'Source Sans 3' }}>
                  VCU Children's Hospital
                </p>
              </motion.div>
            </div>

            <motion.div
              className="mt-12 bg-[#FAF8F5] rounded-xl p-8 border border-[rgba(26,46,74,0.08)]"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
                <div>
                  <div className="text-3xl font-bold text-[#2d7d8a] mb-2" style={{ fontFamily: 'Playfair Display' }}>
                    $180K+
                  </div>
                  <p className="text-[#1a2e4a] font-medium" style={{ fontFamily: 'Source Sans 3' }}>
                    Annual Cost Savings
                  </p>
                </div>
                <div>
                  <div className="text-3xl font-bold text-[#2d7d8a] mb-2" style={{ fontFamily: 'Playfair Display' }}>
                    12,000+
                  </div>
                  <p className="text-[#1a2e4a] font-medium" style={{ fontFamily: 'Source Sans 3' }}>
                    Employees Impacted
                  </p>
                </div>
                <div>
                  <div className="text-3xl font-bold text-[#2d7d8a] mb-2" style={{ fontFamily: 'Playfair Display' }}>
                    4
                  </div>
                  <p className="text-[#1a2e4a] font-medium" style={{ fontFamily: 'Source Sans 3' }}>
                    Top Healthcare Systems
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="bg-gradient-to-br from-[#1A3A52] via-[#1a4a5e] to-[#0A6B7C] py-16 md:py-20">
          <div className="max-w-4xl mx-auto px-6 md:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-display text-3xl md:text-4xl font-semibold text-white mb-6">
                Ready to Transform Your Organization?
              </h2>
              <p className="font-body text-lg text-white/85 leading-relaxed mb-8">
                These case studies represent what's possible when healthcare organizations embrace both the science of medicine and the art of human connection. Let's explore what transformation could look like for your team.
              </p>
              <a
                href="/contact"
                className="inline-block bg-white text-[#1A3A52] font-body font-semibold px-8 py-4 rounded-lg hover:bg-[#FAF8F5] transition-all duration-300 hover:scale-105 shadow-lg"
              >
                Start a Conversation
              </a>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CaseStudiesPage;